import { useCallback, useEffect, useRef, useState } from "react"
import useCloseRequest from "../hooks/useCloseRequest"
import "./IdleLock.css"

// A tablet left on a pass or a bar top should not sit showing a menu item all
// night. After this long untouched the app veils itself; any touch brings it back.
const IDLE_MS = 60_000

// Passive so none of these can delay scrolling on the menu list.
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll"]

// How long to keep watching for the click iOS synthesises after the dismissing touch.
const SWALLOW_CLICK_MS = 400

export default function IdleLock() {
  const [isLocked, setIsLocked] = useState(false)
  const timerRef = useRef(null)

  const clear = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const arm = useCallback(() => {
    clear()
    timerRef.current = window.setTimeout(() => setIsLocked(true), IDLE_MS)
  }, [clear])

  // The gesture that dismisses the veil must not reach the menu underneath: the veil is
  // gone by the time iOS synthesises the click, so it would land on whatever row was
  // under the finger. Swallow that one follow-up click.
  const unlock = useCallback((event) => {
    event.preventDefault()

    const swallowFollowUpClick = (followUp) => {
      followUp.preventDefault()
      followUp.stopPropagation()
    }
    window.addEventListener("click", swallowFollowUpClick, { capture: true, once: true })
    // A keyboard or trackpad dismissal has no click to swallow; drop the trap either way.
    window.setTimeout(
      () => window.removeEventListener("click", swallowFollowUpClick, { capture: true }),
      SWALLOW_CLICK_MS
    )

    setIsLocked(false)
  }, [])

  // Back lifts the veil like a touch, rather than acting on the screen hidden under it.
  useCloseRequest(isLocked, () => setIsLocked(false))

  useEffect(() => {
    // While locked the countdown stops — the veil is dismissed by touch, not by a timer.
    if (isLocked) {
      clear()
      return undefined
    }

    arm()
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, arm, { passive: true })
    )

    // Backgrounding the app should not burn the idle window; restart on return.
    const onVisibility = () => {
      if (document.visibilityState === "visible") arm()
      else clear()
    }
    document.addEventListener("visibilitychange", onVisibility)

    return () => {
      clear()
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, arm))
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [isLocked, arm, clear])

  if (!isLocked) return null

  return (
    <div
      className="idle-lock"
      role="button"
      tabIndex={0}
      aria-label="Tap to unlock"
      onPointerUp={unlock}
      onKeyDown={unlock}
    >
      <div className="idle-lock__inner">
        <img className="idle-lock__mark" src="/img/vantero-wordmark.svg" alt="Vantero Hospitality" />
        <p className="idle-lock__hint">Touch anywhere to continue</p>
      </div>
    </div>
  )
}
